import React, { useState, useEffect } from 'react';
import { PayoutConfig } from '../utils/instructions';
import { BASIS_POINTS_DIVISOR, scaleDown, scaleUp } from '../utils/scale';
import { MONOPOLY_DECIMALS } from '../../solana-constants';

interface PayoutMapEditorProps {
  currentPayoutMap: PayoutConfig[] | null;
  onSave: (payoutMap: PayoutConfig[]) => Promise<void>;
}

const PayoutMapEditor: React.FC<PayoutMapEditorProps> = ({ currentPayoutMap, onSave }) => {
  const [payoutMap, setPayoutMap] = useState<PayoutConfig[]>([]);
  const [tempDice1, setTempDice1] = useState<number>(1);
  const [tempDice2, setTempDice2] = useState<number>(1);
  const [tempPayoutType, setTempPayoutType] = useState<number>(0);
  const [tempPayoutValue, setTempPayoutValue] = useState<string>("0");
  const [isSaving, setIsSaving] = useState(false);

  // Initialize with current payout map when it loads
  useEffect(() => {
    if (currentPayoutMap) {
      setPayoutMap(currentPayoutMap);
    }
  }, [currentPayoutMap]);

  const handleAddPayoutConfig = () => {
    const newConfig: PayoutConfig = {
      dice_1: tempDice1,
      dice_2: tempDice2,
      payout_type: tempPayoutType,
      payout_value: BigInt(scaleUp(tempPayoutValue, MONOPOLY_DECIMALS) || "0"),
    };
    setPayoutMap([...payoutMap, newConfig]);
    
    // Reset form
    setTempDice1(1);
    setTempDice2(1);
    setTempPayoutValue("0");
  };

  const handleRemoveConfig = (index: number) => {
    setPayoutMap(payoutMap.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(payoutMap);
    } catch (error) {
      console.error('Error saving payout map:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mb-8 p-6 bg-gray-800 rounded-xl">
      <h3 className="text-xl font-bold mb-4 text-gray-200">Update Payout Map</h3>
      
      {/* Add New Configuration */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Dice 1:</label>
          <input
            type="number"
            min="1"
            max="6"
            className="w-16 p-2 bg-gray-700 text-white rounded border border-gray-600"
            value={tempDice1}
            onChange={(e) => setTempDice1(Number(e.target.value))}
          />
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Dice 2:</label>
          <input
            type="number"
            min="1"
            max="6"
            className="w-16 p-2 bg-gray-700 text-white rounded border border-gray-600"
            value={tempDice2}
            onChange={(e) => setTempDice2(Number(e.target.value))}
          />
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Type:</label>
          <select
            className="p-2 bg-gray-700 text-white rounded border border-gray-600"
            value={tempPayoutType}
            onChange={(e) => setTempPayoutType(Number(e.target.value))}
          >
            <option value={0}>Fixed</option>
            <option value={1}>Percentage</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Value:</label>
          <input
            type="number"
            className="w-32 p-2 bg-gray-700 text-white rounded border border-gray-600"
            value={tempPayoutValue}
            onChange={(e) => setTempPayoutValue(e.target.value)}
          />
        </div>
        
        <button
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          onClick={handleAddPayoutConfig}
        >
          Add Configuration
        </button>
      </div>

      {/* Current Configurations */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-2 text-gray-300">Current Configurations</h4>
        <div className="space-y-2">
          {payoutMap.map((cfg, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-700 rounded">
              <span className="text-gray-200">
                Roll {cfg.dice_1} + {cfg.dice_2} →{' '}
                {cfg.payout_type === 0 ? (
                  <span>{scaleDown(cfg.payout_value.toString(), MONOPOLY_DECIMALS)} $MONOPOLY</span>
                ) : (
                  <span>{((Number(cfg.payout_value)/Number(BASIS_POINTS_DIVISOR))*100).toString()}%</span>
                )}
              </span>
              <button
                onClick={() => handleRemoveConfig(idx)}
                className="px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        className="w-full px-6 py-3 bg-accent-600 text-white rounded-xl hover:bg-accent-700 transition-colors disabled:opacity-50"
        onClick={handleSave}
        disabled={isSaving}
      >
        {isSaving ? 'Saving...' : 'Save Payout Map'}
      </button>
    </div>
  );
};

export default PayoutMapEditor;
